import { Currencies } from 'app/appState/model/types';
import { AppLanguage } from 'app/config/i18Config/types';
import { Dayjs } from 'dayjs';
import { CachedBox } from 'entities/Box';
import { Deposit } from 'entities/Deposit';
import { Discount } from 'entities/Discount';
import { EmailType } from 'entities/Email';
import { Insurance } from 'entities/Insurance';
import { Invoice, InvoiceFrequencyType } from 'entities/Invoice';
import { RentalItem } from 'entities/RentalItem';
import { RentOptionDocument } from 'entities/RentOption';
import { User, UserAccountType } from 'entities/User';
import { VatRate } from 'entities/VatRate';
import { Warehouse } from 'entities/Warehouse';

export type PaymentFrequencyPeriod = 'regular' | 'entire period';

export enum UnitType {
  BOX = 'Box',
  OFFICE = 'Office',
  INSURANCE = 'Insurance',
  PARKING_PLACE = 'ParkingPlace',
  POSTAL_BOX = 'PostalBox',
  RENTING_STUFF = 'RentingStuff',
  DEPOSIT = 'Deposit',
}

export enum AutochargeStatus {
  ENABLED = 'enabled',
  DISABLED = 'disabled',
  STOPPED = 'stopped',
}

export enum ContractUnitType {
  BOX = 'Box',
  OFFICE = 'Office',
  INSURANCE = 'Insurance',
  PARKING_PLACE = 'ParkingPlace',
  POSTAL_BOX = 'PostalBox',
  RENTING_STUFF = 'RentingStuff',
  DEPOSIT = 'Deposit',
}

export interface ContractPeriodsUnitPrice {
  periodNumber: number;
  rate: number;
  discountAmount: number;
  discountPercent: number;
  initialRate: number;
}

export interface Contract {
  contractId: string;
  contractNumber: number;
  warehouse: Warehouse;
  user: User;
  startDate: string;
  initialEndDate: Dayjs;
  endDate: Dayjs;
  isPrePaymentApplied: boolean;
  noticeLeaveDate: Dayjs;
  noticeGivenDate: Dayjs;
  leaveDate: Dayjs;
  chargedTo: string;
  contractItems?: ContractItem[];
  invoiceFrequencyType: InvoiceFrequencyType;
  invoiceFrequency: number;
  isContractSigned: boolean;
  isApplicable: boolean;
  contractSignId: Nullable<string>;
  invoiceLeadDays: number;
  notes: Nullable<string>;
  accessDenied: boolean;
  autocharges: AutochargeStatus;
  currency: string;
  createdAt: string;
  updatedAt: string;
  invoices?: Invoice[];
  deposit?: Deposit;
  description?: string;
  isAutoClosingIgnored: boolean;
  isLateChargeIgnored: boolean;
  closedBy?: User;
  createdBy?: User;
  closingWithoutVerificationReason?: ClosingWithoutVerificationReason;
}

export interface ContractWithBookingInfo {
  contractId: string;
  contractNumber: string;
  startDate: string;
  initialEndDate: string;
  endDate: string;
  isPrePaymentApplied: boolean;
  chargedTo: string;
  invoiceFrequencyType: InvoiceFrequencyType;
  invoiceFrequency: number;
  isContractSigned: boolean;
  accessDenied: boolean;
  autocharges: AutochargeStatus;
  currency: string;
  isContractPaid: boolean;
  contractDebt: number;
  paidDays: number;
  nextInvoiceDueDate?: string;
  nextInvoiceAmount?: string;
  discountKeys?: string[];
  invoiceLeadDays: number;
}

export interface ContractItem {
  contractItemId: string;
  contractUnitType: ContractUnitType;
  box: Nullable<CachedBox>;
  insurance: Nullable<Insurance>;
  rentalItem: Nullable<RentalItem>;
  vatRate: VatRate;
  isOneTimePayment: boolean;
  oneTimePaymentAmount: number;
  isStandalone: boolean;
  description: Nullable<string>;
}

export interface ContractOptionsSchema {
  contractOptions: ContractInfo;
}

export interface ContractInfo {
  accountType?: UserAccountType;
  contractDuration: number;
  invoiceFrequencyType: InvoiceFrequencyType;
  startDate?: Nullable<Dayjs>;
  endDate?: Nullable<Dayjs>;
  insuranceId: Nullable<string>;
  insuranceAmountWithVat: number;
  insuranceAmountWithoutVat: number;
  insuranceVatRatePercent: number;
  insuranceVatRateAmount: number;
  insuranceRate: number;
  coverageAmount: Nullable<number>;
  depositAmount: number;
  payForEntirePeriod: boolean;
  totalToPay: number;
  balanceToChargeAmount: number;
  totalToCharge: number;
  contractPeriodsUnitPrices: ContractPeriodsUnitPrice[];
  vatRatePercent: number;
  vatRateAmount: number;
  discountPercent: number;
  discountAmount: number;
  rentOption?: Nullable<RentOptionDocument>;
  promoCodeValue: Nullable<string>;
  additionalDiscount?: Nullable<Discount>;
}

export interface GenerateContractParams {
  warehouseName: string;
  warehouseAddress: string;
  startDate: string;
  boxId: string;
  boxNumber: string;
  boxSquare: number;
  invoiceFrequencyType: InvoiceFrequencyType;
  contractDuration: number;
  clientFirstName: string;
  clientLastName: string;
  clientStreet: string;
  clientCountry: string;
  clientCity: string;
  clientHouse: string;
  clientApartment?: string;
  clientZip: string;
  language: string;
  clientEmail: string;
  clientPhone: string;
  rentalCost: string;
  discountPercent: number;
  discountAmount: number;
  insuranceAmount: number;
  vatRateAmount: number;
  totalToPay: number;
  paymentFrequencyPeriod: PaymentFrequencyPeriod;
  currency: Currencies;
  insuranceVatRateAmount: number;
  depositAmount?: number;
  rentalItems?: RentalItem[];
  vatRatePercent: number;
  insuranceVatRatePercent: number;
}

export interface CreateContractParams {
  userId: string;
  warehouseId: string;
  insuranceId?: string;
  rentOptionId: string;
  startDate: string;
  endDate?: string;
  invoiceFrequencyType: InvoiceFrequencyType;
  invoiceFrequency: number;
  paymentFrequencyPeriod: PaymentFrequencyPeriod;
  useUserBalance: boolean;
  promoCode?: Nullable<string>;
  additionalDiscountId?: Nullable<string>;
  rentalItems?: RentalItem[];
}

export interface UpdateContractParams {
  noticeLeaveDate?: string;
  noticeGivenDate?: string;
  leaveDate?: string;
  chargedTo?: string;
  isContractSigned?: boolean;
  contractSignId?: string;
  notes?: string;
  isAutoClosingIgnored?: boolean;
  isLateChargeIgnored?: boolean;
}

export interface CloseContractParams {
  issueUnpaidInvoices: boolean;
  enableDepositCharge: boolean;
  enableUserBalanceCharge: boolean;
  ignoreMinContractDuration: boolean;
  refundOptions: CloseContractRefundOption;
  leaveDate: string;
  emailType: EmailType;
  closingWithoutVerificationReason?: ClosingWithoutVerificationReason;
}

export interface NotifyLeaveDateParams {
  contractId: string;
  noticeLeaveDate: string;
}

export enum ClosingWithoutVerificationReason {
  UNIT_NOT_OPENED = 'unit not opened',
  INSPECTED_BY_EMPLOYEE = 'inspected by employee',
  UNIT_REPLACED_ON_MOVE_IN = 'unit replaced on move-in',
  DEBTORS_STUFF = 'debtors stuff',
  PHOTOS_SENT_TO_EMAIL = 'photos were sent to email',
}

export interface NotifyLeaveDaysLimits {
  [InvoiceFrequencyType.DAY]: number;
  [InvoiceFrequencyType.WEEK]: number;
  [InvoiceFrequencyType.MONTH]: number;
}

export interface SendContractClosureNotificationParams {
  contractId: string;
  language: AppLanguage;
  reason: string;
}

export type CloseContractRefundOption = 'default' | 'refund-all' | 'no-refunds';

export interface CloseContractPreviewParams {
  leaveDate: string;
  refundOptions: CloseContractRefundOption;
  issueUnpaidInvoices: boolean;
  enableDepositCharge: boolean;
  enableUserBalanceCharge: boolean;
  ignoreMinContractDuration: boolean;
}

export interface PreviewOptionTotals {
  refundAmount: number;
  penaltyAmount: number;
}

export interface PreviewInvoiceBreakdown {
  invoiceId: string;
  documentNumber: string;
  dateFrom: Nullable<string>;
  dateTo: Nullable<string>;
  totalAmount: number;
  paidAmount: number;
  usedAmount: number;
  unusedAmount: number;
  creditNoteAmount: number;
  depositChargeAmount: number;
  balanceChargeAmount: number;
  penaltyAmount: number;
  isDepositInvoice: boolean;
  isAffected: boolean;
}

export interface PreviewRefund {
  paymentId: string;
  documentNumber: string;
  paymentMethod: string;
  paidAt: Nullable<string>;
  amount: number;
  isDepositRefund: boolean;
}

export interface PreviewPenaltyInvoice {
  items: Array<{ description: string; amount: number }>;
  totalAmount: number;
  dueDate: string;
}

export interface PreviewDeposit {
  invoiceNumber: Nullable<string>;
  paidAmount: number;
  chargedAmount: number;
  refundAmount: number;
}

export interface CloseContractPreview {
  contractId: string;
  closeDate: string;
  currency: string;
  totals: PreviewOptionTotals;
  invoices: PreviewInvoiceBreakdown[];
  deposit: Nullable<PreviewDeposit>;
  userBalance: { available: number; chargedAmount: number };
  refunds: PreviewRefund[];
  penaltyInvoice: Nullable<PreviewPenaltyInvoice>;
  meta: { calculatedAt: string };
}

export enum PriceRaiseTab {
  UNPROCESSED = 'unprocessed',
  PROCESSED = 'processed',
  COMPLETED = 'completed',
  UNFINISHED = 'unfinished',
}

export enum ContractItemPriceRaiseStatus {
  ACCEPTED = 'accepted',
  REJECTED = 'rejected',
  REVIEW_AGAIN = 'review_again',
  COMPLETED = 'completed',
  UNFINISHED = 'unfinished',
}

export enum PriceRaiseEligibleStatusFilter {
  REVIEW_AGAIN = 'review_again',
  HAD_COMPLETED = 'had_completed',
}

export interface ContractItemPriceRaise {
  contractItemPriceRaiseId: string;
  contract: Contract;
  contractItem?: ContractItem;
  oldPrice: number;
  newPrice: number;
  priceRaiseDate?: string;
  nextReviewDate?: string;
  status: ContractItemPriceRaiseStatus;
  notificationSentAt?: string;
  createdAt: string;
}

export interface AcceptContractItemPriceRaiseDto {
  oldPrice: number;
  newPrice: number;
  contractItemId?: string;
}

export interface RejectContractItemPriceRaiseDto {
  months: number;
  oldPrice: number;
  newPrice: number;
  contractItemId?: string;
}

export interface UpdateContractItemPriceRaiseDto {
  contractItemId?: string;
  oldPrice?: number;
  newPrice?: number;
  status?: ContractItemPriceRaiseStatus;
  months?: number;
}

export interface ContractEligibleForPriceRaise extends Contract {
  priceRaiseDate?: string;
  contractItemPriceRaises?: ContractItemPriceRaise[];
}
